import PSPDFKit from "pspdfkit";

let signatureAnchor = null;
let launchingSignatureFromPlaceholder = false;
let signatureText = null;

const SIGNATURE_TIMESTAMP_FLAG = "signatureTimestamp";
const SIGNATURE_TIMESTAMP_FOR = "signatureTimestampFor";

function toAnnotationArray(annotations) {
  if (!annotations) return [];
  if (typeof annotations.toArray === "function") return annotations.toArray();
  if (Array.isArray(annotations)) return annotations;

  const result = [];
  if (typeof annotations.forEach === "function") {
    annotations.forEach(annotation => result.push(annotation));
  }
  return result;
}

function formatInkSignatureTimestamp(date = new Date()) {
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "long" });
  const year = date.getFullYear();
  const time = date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return `Signed on ${day} ${month} ${year} at ${time}`;
}

function computeTimestampRectBelow(signatureRect) {
  if (!signatureRect) return null;

  return new PSPDFKit.Geometry.Rect({
    left: signatureRect.left,
    top: signatureRect.top + signatureRect.height + 6,
    width: Math.max(signatureRect.width, 220),
    height: 18,
  });
}

async function upsertInkSignatureTimestamp(instance, signatureAnnotation) {
  if (!signatureAnnotation?.boundingBox) return null;

  const pageIndex = signatureAnnotation.pageIndex;
  const signatureId = signatureAnnotation.id;
  const timestampText = formatInkSignatureTimestamp(new Date());
  const timestampRect = computeTimestampRectBelow(signatureAnnotation.boundingBox);

  if (!timestampRect) return null;

  const pageAnnotations = toAnnotationArray(await instance.getAnnotations(pageIndex));

  const existingTimestamp = pageAnnotations.find(annotation =>
    annotation.customData?.[SIGNATURE_TIMESTAMP_FOR] === signatureId
  );

  if (existingTimestamp) {
    const updatedTimestamp = existingTimestamp.set("boundingBox", timestampRect).set("text", { format: "plain", value: timestampText });

    await instance.update(updatedTimestamp);
    return updatedTimestamp;
  }

  const timestampAnnotation = new PSPDFKit.Annotations.TextAnnotation({
    pageIndex,
    boundingBox: timestampRect,
    text: { format: "plain", value: timestampText },
    fontSize: 10,
    isBold: false,
    customData: {
      [SIGNATURE_TIMESTAMP_FLAG]: true,
      [SIGNATURE_TIMESTAMP_FOR]: signatureId,
    },
  });

  await instance.create(timestampAnnotation);
  return timestampAnnotation;
}

async function deleteLinkedInkSignatureTimestamps(instance, deletedAnnotations) {
  const deletedArray = toAnnotationArray(deletedAnnotations);

  for (const deletedAnnotation of deletedArray) {
    const isSignature =
      deletedAnnotation?.isSignature || deletedAnnotation?.customData?.manualSignature;

    if (!isSignature) continue;

    const pageAnnotations = toAnnotationArray(
      await instance.getAnnotations(deletedAnnotation.pageIndex)
    );

    const linkedTimestamps = pageAnnotations.filter(annotation =>
      annotation.customData?.[SIGNATURE_TIMESTAMP_FOR] === deletedAnnotation.id
    );

    for (const timestampAnnotation of linkedTimestamps) {
      await instance.delete(timestampAnnotation).catch(() => {});
    }
  }
}

async function openSignaturePickerUI(instance) {
  const doc = instance.contentDocument;
  if (!doc) return false;

  const wait = (ms) => new Promise(r => setTimeout(r, ms));

  let annotationsBtn = doc.querySelector('button[aria-label="Annotations"]') || doc.querySelector('button[title="Annotations"]') || doc.querySelector('.PSPDFKit-Toolbar-Button-Annotate');
  if (annotationsBtn) {
    annotationsBtn.click();

    for (let i = 0; i < 10; i++) {
      const sigBtn = doc.querySelector('button.PSPDFKit-Toolbar-Button-InkSignature-Annotation[aria-label="Sign"]');
      if (sigBtn) break;
      await wait(40);
    }
  }

  let overflowBtn = doc.querySelector("button[aria-label='More']") || doc.querySelector("[data-testid='overflow-menu-button']") || doc.querySelector(".PSPDFKit-Toolbar-Button-Overflow");
  if (overflowBtn) {
    overflowBtn.click();
    // Wait for menu to render
    for (let i = 0; i < 10; i++) {
      const sigBtn = doc.querySelector('button.PSPDFKit-Toolbar-Button-InkSignature-Annotation[aria-label="Sign"]');
      if (sigBtn) break;
      await wait(40);
    }
  }

  const sigBtn = doc.querySelector('button.PSPDFKit-Toolbar-Button-InkSignature-Annotation[aria-label="Sign"]');
  if (sigBtn) {
    sigBtn.click();
  } else {
    console.debug("[signature][picker] could not find signature tool button after annotations");
    return false;
  }

  for (let i = 0; i < 20; i++) {
    if (doc.querySelector(".PSPDFKit-SignaturePicker")) return true;
    await wait(50);
  }

  console.debug("[signature][picker] could not open picker UI");
  return false;
}

async function openAddInkSignatureModal(instance) {
  const doc = instance.contentDocument;
  if (!doc) return false;
  const wait = (ms) => new Promise(r => setTimeout(r, ms));

  const isSignatureDialogOpen = () =>
    !!(
      doc.querySelector('[role="dialog"][aria-label="Signature"]') ||
      doc.querySelector('.PSPDFKit-Modal .PSPDFKit-SignaturePicker') ||
      doc.querySelector('.PSPDFKit-SignatureModal')
    );

  // Ensure picker/dialog present
  if (!doc.querySelector(".PSPDFKit-SignaturePicker") && !isSignatureDialogOpen()) {
    await openSignaturePickerUI(instance);
  }

  for (let i = 0; i < 40; i++) {
    // If dialog already open (with picker or canvas) we are done
    if (isSignatureDialogOpen()) {
      console.debug("[signature] Signature dialog already open.");
      return true;
    }

    const addBtn = doc.querySelector('[aria-label="Add Signature"]') || doc.querySelector('[data-testid="signature-add-button"]') || doc.querySelector(".PSPDFKit-SignaturePicker-AddButton");
    if (addBtn) {
      addBtn.click();
      console.debug("[signature] Clicked Add Signature (+) button.");
      // Wait a short moment for dialog
      for (let j = 0; j < 10; j++) {
        if (isSignatureDialogOpen()) return true;
        await wait(40);
      }
      return true;
    }

    // Re-open picker if it vanished
    if (!doc.querySelector(".PSPDFKit-SignaturePicker") && i % 5 === 0) {
      await openSignaturePickerUI(instance);
    }
    await wait(60);
  }
  console.debug("[signature] Could not open Add Signature modal.");
  return false;
}

export async function attachListeners(instance, buttons, toolbarItems, activateSigningButtons) {
  instance.addEventListener("document.change", async () => {
    // Enable the "Reset" button if the document is signed
    const annotations = await instance.getAnnotations(0);
    updateToolbarItems(annotations, true, instance, buttons, toolbarItems, activateSigningButtons);
  });

  instance.addEventListener("annotations.create", async (createdAnnotations) => {
    const signatures = await instance.getSignaturesInfo().catch(() => ({ status: "not_signed" }));
    const isDocumentSigned = signatures.status !== "not_signed";
    if (isDocumentSigned) return;

    const annotation = createdAnnotations.get(0);
    if (!annotation) return;

    const isSig = annotation.isSignature || annotation.customData?.manualSignature;
    if (isSig) {
      const finalSignatureAnnotation = !(signatureAnchor?.mode === "placeholder" && annotation.customData?.manualSignature) ? await positionSignature(annotation, instance) : annotation;
      const signatureAnnotationForTimestamp = finalSignatureAnnotation?.boundingBox ? finalSignatureAnnotation : annotation;

      // After positioning, remove the placeholder (now that signature exists)
      if (signatureAnchor?.mode === "placeholder") {
        try {
          const pageAnns = await instance.getAnnotations(signatureAnchor.pageIndex);
          pageAnns.forEach(a => {
            if (a.customData?.signaturePlaceholder) {
              instance.delete(a).catch(() => {});
            }
          });
        } catch {}
      }

      const timestampAnnotation = await upsertInkSignatureTimestamp(
        instance,
        signatureAnnotationForTimestamp
      );

      await Promise.all([
        instance.ensureChangesSaved(signatureAnnotationForTimestamp).catch(() => {}),
        timestampAnnotation ? instance.ensureChangesSaved(timestampAnnotation).catch(() => {}) : Promise.resolve()
      ]);

      updateToolbarItems(createdAnnotations, true, instance, buttons, toolbarItems, activateSigningButtons);

      // Re-click the annotations icon after signing
      setTimeout(() => exitAnnotationMode(instance, true), 200);
    }
  });

  // Only attach once per instance
  if (!instance._signatureModalObserverAttached) {
    instance._signatureModalObserverAttached = true;
    const doc = instance.contentDocument;
    if (doc) {
      let wasModalOpen = false;
      const observer = new MutationObserver(() => {
        const modalOpen =
          doc.querySelector('[role="dialog"][aria-label="Signature"]') ||
          doc.querySelector('.PSPDFKit-Modal .PSPDFKit-SignaturePicker') ||
          doc.querySelector('.PSPDFKit-SignatureModal');

        if (wasModalOpen && !modalOpen) {
          // Modal just closed
          setTimeout(() => exitAnnotationMode(instance, true), 100);
        }

        wasModalOpen = !!modalOpen;
      });
      observer.observe(doc.body, { childList: true, subtree: true });
    }
  }

  instance.addEventListener("annotations.delete", async (deletedAnnotations) => {
    const annotations = await instance.getAnnotations(0);
    updateToolbarItems(annotations, false, instance, buttons, toolbarItems, activateSigningButtons);
    await deleteLinkedInkSignatureTimestamps(instance, deletedAnnotations);
    
    let annArray = [];
    if (annotations) {
      if (typeof annotations.toArray === "function") annArray = annotations.toArray();
      else if (Array.isArray(annotations)) annArray = annotations;
      else if (typeof annotations.forEach === "function") {
        annotations.forEach(a => annArray.push(a));
      }
    }

    const hasInkSignature = annArray.some(
      a => a.isSignature || a.customData?.manualSignature
    );

    let hasPlaceholder = false;
    if (signatureAnchor && signatureAnchor.pageIndex != null) {
      const pageAnns = await instance.getAnnotations(signatureAnchor.pageIndex);
      hasPlaceholder = pageAnns.some(a => a.customData?.signaturePlaceholder);
    }

    let deletedWasSignature = false;
    if (deletedAnnotations) {
      let deletedArr = [];
      if (typeof deletedAnnotations.toArray === "function") deletedArr = deletedAnnotations.toArray();
      else if (Array.isArray(deletedAnnotations)) deletedArr = deletedAnnotations;
      else if (typeof deletedAnnotations.forEach === "function") {
        deletedAnnotations.forEach(a => deletedArr.push(a));
      }

      deletedWasSignature = deletedArr.some(
        a => a.isSignature || a.customData?.manualSignature
      );
    }

    // Only restore placeholder if:
    // - No signature present
    // - Placeholder does not exist
    // - signatureText and signatureAnchor are available
    // - The deleted annotation was a signature
    if (!hasInkSignature && !hasPlaceholder && signatureText && signatureAnchor && deletedWasSignature) {
      await positionSignatureElement(instance, signatureText);
    }
  });

  const annotations = await instance.getAnnotations(0);
  updateToolbarItems(annotations, true, instance, buttons, toolbarItems, activateSigningButtons);

  // Handle activating signature modal when placeholder is pressed
  const activatePlaceholderSignature = async (ann) => {
    if (!ann?.customData?.signaturePlaceholder) return;
    if (launchingSignatureFromPlaceholder) return;
    launchingSignatureFromPlaceholder = true;

    const tempAnchor = {
      pageIndex: ann.pageIndex,
      rect: ann.boundingBox,
      mode: "placeholder"
    };

    try {
      // Store anchor (do NOT delete placeholder yet)
      signatureAnchor = tempAnchor;

      // Open picker/dialog WITHOUT switching interactionMode (avoids auto-close)
      const docOpened = await openSignaturePickerUI(instance);
      if (!docOpened) {
        console.debug("[signature][placeholder] picker not opened, attempting modal directly.");
      }

      await openAddInkSignatureModal(instance);
    } finally {
      setTimeout(() => { launchingSignatureFromPlaceholder = false; }, 300);
    }
  };

  instance.addEventListener("annotations.press", (event) => {
    const ann = event?.annotation || (event && typeof event.get === "function" ? event.get(0) : null);
    activatePlaceholderSignature(ann);
  });

  instance.addEventListener("annotationSelection.change", (event) => {
    const list = event?.newValue || event;
    const ann = list && typeof list.get === "function" ? list.get(0) : Array.isArray(list) ? list[0] : null;
    activatePlaceholderSignature(ann);
  });

  return instance;
}

function computeSignatureRectBeside(anchorRect, pageInfo, opts = {}) {
  const margin = opts.margin ?? 38;  
  const extraGap = opts.extraLeftGap ?? 24; 
  const desiredWidth = opts.width ?? 200;
  const desiredHeight = opts.height ?? Math.max(Math.min(anchorRect.height * 1.6, 60), anchorRect.height + 6);

  const pageWidth = pageInfo?.width || (anchorRect.left + anchorRect.width + desiredWidth + margin + extraGap);

  let left = anchorRect.left + anchorRect.width + margin + extraGap;
  let top = anchorRect.top + (anchorRect.height - desiredHeight) / 2;

  const fitsRight = left + desiredWidth <= pageWidth;
  if (!fitsRight) {
    // Try left side (no extra gap when going left)
    const leftAlt = anchorRect.left - margin - desiredWidth;
    if (leftAlt >= 0) {
      left = leftAlt;
    } else {
      // Fallback: place below
      left = anchorRect.left;
      top = anchorRect.top + anchorRect.height + margin;
    }
  }

  if (top < 0) top = 0;

  return new PSPDFKit.Geometry.Rect({
    left,
    top,
    width: desiredWidth,
    height: desiredHeight
  });
}

export async function positionSignatureElement(instance, text) {
  if (!text) return;
  signatureText = text;

  let results = await instance.search(text);
  if (results && typeof results.toArray === "function") results = results.toArray();

  const count = Array.isArray(results) ? results.length : (typeof results?.size === "number" ? results.size : 0);
  if (!count) return;

  const firstResult = Array.isArray(results) ? results[0] : results.get(0);
  const pageIndex = firstResult.pageIndex;

  const rectsList = firstResult.rectsOnPage;
  if (!rectsList || (rectsList.size === 0 && !Array.isArray(rectsList))) return;

  const unionInput = (rectsList && typeof rectsList.toArray === "function") ? rectsList : PSPDFKit.Immutable.List(rectsList);
  const targetRect = PSPDFKit.Geometry.Rect.union(unionInput);

  // Store anchor for later (user signature reposition)
  signatureAnchor = { pageIndex, rect: targetRect, mode: "textAnchor" };

  const pageInfo = instance.pageInfoForIndex(pageIndex);
  const signatureBoundingBox = computeSignatureRectBeside(targetRect, pageInfo);

  // Create a visible placeholder the user can click to start signing.
  const placeholder = new PSPDFKit.Annotations.RectangleAnnotation({
    pageIndex,
    boundingBox: signatureBoundingBox,
    strokeColor: PSPDFKit.Color.fromHex("#1976d2"),
    fillColor: PSPDFKit.Color.fromHex("#1976d218"),
    strokeWidth: 1.5,
    customData: { signaturePlaceholder: true },
    name: "Signature Placeholder"
  });

  await instance.create(placeholder);
  await manualScrollToRect(instance, pageIndex, signatureBoundingBox);
}

async function positionSignature(annotation, instance) {
  if (!signatureAnchor) {
    const signingSpace = new PSPDFKit.Geometry.Rect({
      width: 150,
      height: 40,
      left: 306.0,
      top: 512.097
    });

    const newSize = fitIn(
      {
        width: annotation.boundingBox.width,
        height: annotation.boundingBox.height
      },
      {
        width: signingSpace.width,
        height: signingSpace.height
      }
    );

    const ratio = newSize.width / annotation.boundingBox.width;
    const left = signingSpace.left + (signingSpace.width - newSize.width) / 2;
    const top = signingSpace.top + (signingSpace.height - newSize.height) / 2;

    return updateInkGeometry(annotation, instance, left, top, newSize.width, newSize.height, ratio);
  }

  const { pageIndex, rect: anchorRect, mode } = signatureAnchor;

  // Skip reposition for manual inside placeholder (already sized)
  if (mode === "placeholder" && annotation.customData?.manualSignature) {
    return annotation;
  }

  if (mode === "placeholder") {
    const targetBox = anchorRect;

    const newSize = fitIn(
      {
        width: annotation.boundingBox.width,
        height: annotation.boundingBox.height
      },
      {
        width: targetBox.width,
        height: targetBox.height
      }
    );

    const ratio = newSize.width / annotation.boundingBox.width;
    const left = targetBox.left + (targetBox.width - newSize.width) / 2;
    const top = targetBox.top + (targetBox.height - newSize.height) / 2;

    return updateInkGeometry(
      annotation.set("pageIndex", pageIndex),
      instance,
      left,
      top,
      newSize.width,
      newSize.height,
      ratio
    );
  }

  const pageInfo = instance.pageInfoForIndex(pageIndex);
  const targetBox = computeSignatureRectBeside(anchorRect, pageInfo, {
    width: 200,
    height: Math.max(Math.min(anchorRect.height * 1.6, 60), anchorRect.height + 6)
  });

  const newSize = fitIn(
    {
      width: annotation.boundingBox.width,
      height: annotation.boundingBox.height
    },
    {
      width: targetBox.width,
      height: targetBox.height
    }
  );

  const ratio = newSize.width / annotation.boundingBox.width;
  const left = targetBox.left + (targetBox.width - newSize.width) / 2;
  const top = targetBox.top + (targetBox.height - newSize.height) / 2;

  return updateInkGeometry(
    annotation.set("pageIndex", pageIndex),
    instance,
    left,
    top,
    newSize.width,
    newSize.height,
    ratio
  );
}

async function updateInkGeometry(annotation, instance, left, top, w, h, ratio) {
  const newBoundingBox = new PSPDFKit.Geometry.Rect({
    left,
    top,
    width: w,
    height: h
  });

  let updatedAnnotation;

  if (annotation.lines) {
    const newLines = annotation.lines.map(line =>
      line.map(point =>
        new PSPDFKit.Geometry.DrawingPoint({
          x: left + (point.x - annotation.boundingBox.left) * ratio,
          y: top + (point.y - annotation.boundingBox.top) * ratio
        })
      )
    );

    updatedAnnotation = annotation.set("boundingBox", newBoundingBox).set("lines", newLines).set("lineWidth", annotation.lineWidth * ratio);
  } else {
    updatedAnnotation = annotation.set("boundingBox", newBoundingBox);
  }

  await instance.update(updatedAnnotation);
  return updatedAnnotation;
}

export async function positionText(instance) {
  // appropiate rect for the space to fit the annotation in
  const textSpace = new PSPDFKit.Geometry.Rect({
    width: 120,
    height: 40,
    left: 480,
    top: 690,
  });

  var textAnnotation = new PSPDFKit.Annotations.TextAnnotation({
    pageIndex: 0,
    boundingBox: textSpace,
    text: { format: "plain", value : "Digitally Signed by Gilbert Were on 08/02/2023 1:58 PM" },
    isBold: true,
    fontSize: 12,
  });

  const newSize = fitIn(
    {
      width: textAnnotation.boundingBox.width,
      height: textAnnotation.boundingBox.height,
    },
    {
      width: textSpace.width,
      height: textSpace.height,
    }
  );
  
  const newLeft = textSpace.left + textSpace.width / 2 - newSize.width / 2;
  const newTop = textSpace.top + textSpace.height / 2 - newSize.height / 2;

  const newTextBoundingBox = new PSPDFKit.Geometry.Rect({
    left: newLeft,
    top: newTop,
    width: newSize.width,
    height: newSize.height,
  });

  await instance.create(textAnnotation.set("boundingBox", newTextBoundingBox));
}

function fitIn(size, containerSize) {
  const { width, height } = size;

  const widthRatio = containerSize.width / width;
  const heightRatio = containerSize.height / height;

  const ratio = Math.min(widthRatio, heightRatio);

  return {
    width: width * ratio,
    height: height * ratio,
  };
}

export const initialToolbarItems = [
  { type: "sidebar-thumbnails" },
  { type: "sidebar-bookmarks" },
  { type: "zoom-in" },
  { type: "zoom-out" },
  { type: "spacer" },
];

function updateToolbarItems(annotations, _enableFinishIfSignature, instance, buttons, toolbarItems, activateSigningButtons) {
  // Normalize annotations list
  let annArray = [];
  if (annotations) {
    if (typeof annotations.toArray === "function") annArray = annotations.toArray();
    else if (Array.isArray(annotations)) annArray = annotations;
    else if (typeof annotations.forEach === "function") {
      annotations.forEach(a => annArray.push(a));
    }
  }

  const hasInkSignature = annArray.some(
    a => a.isSignature || a.customData?.manualSignature
  );

  // Clone button definitions to avoid mutating originals
  const finishBtn = { ...(buttons?.finishButton || {}), disabled: !hasInkSignature };
  const resetBtn = { ...(buttons?.resetButton || {}), disabled: !hasInkSignature };
  const closeBtn = { ...(buttons?.closeButton || {})};

  // Base items (keep existing fixed toolbarItems if provided, else fall back to initialToolbarItems)
  let items = toolbarItems ? [...toolbarItems] : [...initialToolbarItems];

  if (activateSigningButtons) {
    // Add signature tool (always enabled) then Finish + Reset (if they exist)
    items = items.filter(i => i?.name !== 'sign');
    items.push({ type: "signature" });
    
    if (buttons?.finishButton) items.push(finishBtn);
    if (buttons?.resetButton) items.push(resetBtn);
  }

  items = items.filter(i => !(i?.type === closeBtn.type && i?.name === closeBtn.name));
  if (buttons?.closeButton) items.push(closeBtn);

  try {
    instance.setToolbarItems(items);
  } catch (e) {
    console.debug("[signature][toolbar] setToolbarItems error:", e);
  }
}

async function manualScrollToRect(instance, pageIndex, rect) {
  // Ensure the target page is selected so it is rendered
  instance.setViewState(v => v.set("currentPageIndex", pageIndex));
  await new Promise(r => requestAnimationFrame(r));

  const doc = instance.contentDocument;
  if (!doc) {
    console.debug("[signature][scroll] no contentDocument");
    return;
  }

  const pageEl = doc.querySelector(`[data-page-index='${pageIndex}']`);
  const scrollContainer =
    doc.querySelector(".PSPDFKit-ScrollContainer") ||
    doc.scrollingElement ||
    doc.documentElement;

  if (!pageEl || !scrollContainer) {
    console.debug("[signature][scroll] missing pageEl or scrollContainer");
    return;
  }

  // Derive scale between PDF points and rendered pixels
  let scaleY = 1;
  try {
    const pageInfo = instance.pageInfoForIndex(pageIndex);
    if (pageInfo?.height) scaleY = pageEl.clientHeight / pageInfo.height;
  } catch (e) {
    console.debug("[signature][scroll] pageInfo error:", e);
  }

  const targetY = pageEl.offsetTop + rect.top * scaleY - 40;
  scrollContainer.scrollTo({
    top: Math.max(targetY, 0),
    behavior: "smooth"
  });

  // Optional highlight
  const highlightBox = expandRect(rect, 6);
  try {
    const highlight = new PSPDFKit.Annotations.RectangleAnnotation({
      id: PSPDFKit.generateInstantId(),
      pageIndex,
      boundingBox: highlightBox,
      strokeColor: PSPDFKit.Color.fromHex("#ffb300"),
      fillColor: PSPDFKit.Color.fromHex("#ffb30055"),
      strokeWidth: 2
    });
    instance.create(highlight).then(() => setTimeout(() => instance.delete(highlight), 1500));
  } catch (e) {
    console.debug("[signature][scroll] highlight error:", e);
  }
}

function expandRect(r, pad) {
  const left = r.left != null ? r.left : r.x;
  const top = r.top != null ? r.top : r.y;
  return new PSPDFKit.Geometry.Rect({
    left: left - pad,
    top: top - pad,
    width: r.width + pad * 2,
    height: r.height + pad * 2
  });
}

function exitAnnotationMode(instance, skipCloseButton = false) {
  if (instance && instance.setViewState) {
    instance.setViewState(viewState =>
      viewState.set("interactionMode", PSPDFKit.InteractionMode.PAN || "PAN")
    );
  }

  if (skipCloseButton) return;

  setTimeout(() => {
    const doc = instance.contentDocument;
    if (!doc) return;
    
    const subToolbarCloseBtn = doc.querySelector(
      'button.PSPDFKit-Tool-Button[aria-label="Close"], button.PSPDFKit-Tool-Button[title="Close"]'
    );

    if (subToolbarCloseBtn) subToolbarCloseBtn.click();
  }, 120);
}
